/**
 * voice-v8 — Shorts über die Panel-Schnittstelle: was Karte (frontend/lib/karten/shorts.ts) und Werkzeug (tools/shorts.ts)
 * gemeinsam wissen. (22.10.2026, UEBERGABE-6-shorts.md des Panels)
 *
 * Nur feste Wörter und die Kennung eines Vorschlags — nie freier Text. Das Knopf-Ja ist der EINZIGE Weg, auf dem
 * Anlegen, Neu-Erzeugen und Vorlegen ausgeführt werden (tools/shorts.ts `jaPasst`).
 */
import { VORSCHLAG_KENNUNG } from "./training.js";

export { VORSCHLAG_KENNUNG };

/** Die drei Schritte mit Außenwirkung: kosten Geld (anlegen, neu) bzw. schicken Martin eine Mail (vorlegen). */
export const SHORTS_SCHRITTE = ["anlegen", "neu", "vorlegen"] as const;
export type ShortsSchritt = (typeof SHORTS_SCHRITTE)[number];

/** Was der Knopf je Schritt in Worten tut. */
const TAT: Readonly<Record<ShortsSchritt, string>> = {
  anlegen: "Short erzeugen lassen",
  neu: "Short neu erzeugen lassen",
  vorlegen: "Short vorlegen",
};

/** Der Titel der Rückfrage-Karte je Schritt. */
export const SHORTS_KARTEN_TITEL: Readonly<Record<ShortsSchritt, string>> = {
  anlegen: "Short erzeugen lassen?",
  neu: "Short neu erzeugen lassen?",
  vorlegen: "Short vorlegen?",
};

export function istShortsSchritt(x: unknown): x is ShortsSchritt {
  return typeof x === "string" && (SHORTS_SCHRITTE as readonly string[]).includes(x);
}

/** Der feste Wortlaut des Ja-Knopfs — EINE Stelle für Karte und Prüfung. */
export function shortsKnopfJa(schritt: ShortsSchritt, vorschlagId: string): string {
  return `Ja, ${TAT[schritt]} (Vorschlag ${vorschlagId})`;
}

/** Der Nein-Knopf derselben Karte (verwirft nur). */
export function shortsKnopfNein(schritt: ShortsSchritt, vorschlagId: string): string {
  return `Nein, ${TAT[schritt]} nicht (Vorschlag ${vorschlagId})`;
}

/**
 * Die Kosten VOR dem Anlegen. Das Panel nennt `kosten.geschaetzt` erst in der Antwort auf POST /shorts; vorher gibt es nur den
 * Richtwert aus UEBERGABE-6-shorts.md, Abschnitt 1 („etwa 0,20 € je Short“). Er steht ausdrücklich als Richtwert da.
 */
export const SHORTS_KOSTEN_RICHTWERT = "etwa 0,20 € (Richtwert des Panels für Bilder und Stimme; den genauen Betrag nennt es beim Anlegen)";

/**
 * Stände ab `freigegeben` entscheidet allein Martin im Panel (openapi.yaml, ShStatus). Vespera nennt sie nie als eigene Tat und
 * sagt nie, ein Short sei veröffentlicht — sie verweist auf das Panel.
 */
export const SHORTS_MARTINS_STAENDE: ReadonlySet<string> = new Set(["freigegeben", "veroeffentlichung", "veroeffentlicht", "teilweise", "verworfen"]);

/** Der Stand eines Shorts in Worten, für Sätze an Martin. Unbekanntes: „unbekannt“. */
export function shortsStandWort(status: unknown): string {
  switch (status) {
    case "angelegt":
    case "skript":
    case "skript_bereit":
    case "render":
      return "in Arbeit im Panel";
    case "bereit":
      return "fertig zum Ansehen (noch nicht vorgelegt)";
    case "vorgelegt":
      return "dir vorgelegt — ansehen und freigeben kannst nur du im Panel";
    case "fehler":
      return "gescheitert";
    default:
      return typeof status === "string" && SHORTS_MARTINS_STAENDE.has(status) ? "liegt bei dir im Panel — dort entschieden" : "unbekannt";
  }
}
